import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  collection,
  doc,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import { db } from '../firebase-config'; // adjust path if needed
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import SaveIcon from '@mui/icons-material/Save';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';

const DOC_PATH = { collection: 'cutoffNotifications', id: 'globalConfig' };

const initialState = {
  cutoffTime: '22:00',
  timezone: 'Europe/London',
  startOffsetMinutes: 60,
  endOffsetMinutes: 0,
  frequencyMinutes: 15,
  title: 'Order before cutoff',
  body: 'Please place your order before cutoff time.',
  imageUrl: '',
  isActive: false,
};

const CutoffNotificationsSettings = () => {
  const storage = getStorage();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [previewTimes, setPreviewTimes] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');

    try {
      const storageRef = ref(storage, `cutoffNotifications/${Date.now()}.jpg`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      console.log('✅ Uploaded image URL:', url);
      setForm(prev => ({ ...prev, imageUrl: url }));
    } catch (e) {
      console.error('Image upload error:', e);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!form.imageUrl) {
      setForm(prev => ({ ...prev, imageUrl: '' }));
      return;
    }

    setError('');
    try {
      const imageRef = ref(storage, form.imageUrl);
      await deleteObject(imageRef).catch(() => {
        // ignore if not found or permission issue
      });
    } catch (e) {
      console.error('Error deleting image from storage', e);
    } finally {
      setForm(prev => ({ ...prev, imageUrl: '' }));
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNumberChange = (field, value) => {
    if (value === '') {
      setForm(prev => ({ ...prev, [field]: '' }));
      return;
    }
    if (!/^-?\d*$/.test(value)) return;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const loadConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const docRef = doc(collection(db, DOC_PATH.collection), DOC_PATH.id);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        setForm(prev => ({
          ...prev,
          ...data,
        }));
      } else {
        setForm(initialState);
      }
    } catch (e) {
      console.error('Error loading cutoffNotifications config', e);
      setError('Failed to load configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const computePreviewTimes = () => {
    try {
      let {
        cutoffTime,
        startOffsetMinutes,
        endOffsetMinutes,
        frequencyMinutes,
      } = form;

      const [h, m] = String(cutoffTime || '22:00').split(':').map(Number);
      if (isNaN(h) || isNaN(m)) {
        setPreviewTimes([]);
        return;
      }

      const start = parseInt(startOffsetMinutes, 10);
      const end = parseInt(endOffsetMinutes, 10);
      const freq = parseInt(frequencyMinutes, 10);

      if (isNaN(start) || isNaN(end) || isNaN(freq) || freq <= 0) {
        setPreviewTimes([]);
        return;
      }

      const now = new Date();
      const cutoff = new Date(now);
      cutoff.setHours(h, m, 0, 0);

      const list = [];
      for (let offset = start; offset >= end; offset -= freq) {
        const t = new Date(cutoff.getTime() - offset * 60000);
        list.push(t);
      }
      setPreviewTimes(list);
    } catch (e) {
      console.error('Error computing preview times', e);
      setPreviewTimes([]);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    computePreviewTimes();
  }, [form.cutoffTime, form.startOffsetMinutes, form.endOffsetMinutes, form.frequencyMinutes]);

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const docRef = doc(collection(db, DOC_PATH.collection), DOC_PATH.id);

      const payload = {
        cutoffTime: form.cutoffTime || '22:00',
        timezone: form.timezone || 'Europe/London',
        startOffsetMinutes: parseInt(form.startOffsetMinutes, 10) || 0,
        endOffsetMinutes: parseInt(form.endOffsetMinutes, 10) || 0,
        frequencyMinutes: parseInt(form.frequencyMinutes, 10) || 15,
        title: form.title || '',
        body: form.body || '',
        imageUrl: form.imageUrl || '',
        isActive: !!form.isActive,
      };

      await setDoc(docRef, payload, { merge: true });
      setSavedAt(new Date());
    } catch (e) {
      console.error('Error saving cutoffNotifications config', e);
      setError('Failed to save configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={1}>
          <NotificationsActiveIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h1" color="text.primary">
            Cutoff Notifications
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Configure periodic alerts to remind users to place orders before the cutoff time.
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main Configuration Form */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <form onSubmit={handleSave}>
          {/* Status Toggle */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.isActive}
                  onChange={e => handleChange('isActive', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'primary.main',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'primary.main',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body1" fontWeight={500}>
                  Enable cutoff reminder notifications
                </Typography>
              }
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Cutoff Time */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <AccessTimeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="body1" fontWeight={600} color="text.primary">
                Cutoff Time
              </Typography>
            </Stack>
            <TextField
              type="time"
              value={form.cutoffTime}
              onChange={e => handleChange('cutoffTime', e.target.value)}
              fullWidth
              size="small"
              helperText="The time by which orders must be placed"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          </Box>

          {/* Reminder Schedule */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <ScheduleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="body1" fontWeight={600} color="text.primary">
                Reminder Schedule
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Start Offset (minutes)"
                  type="text"
                  value={form.startOffsetMinutes}
                  onChange={e => handleNumberChange('startOffsetMinutes', e.target.value)}
                  fullWidth
                  size="small"
                  helperText="e.g., 60 = start 1 hour before cutoff"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="End Offset (minutes)"
                  type="text"
                  value={form.endOffsetMinutes}
                  onChange={e => handleNumberChange('endOffsetMinutes', e.target.value)}
                  fullWidth
                  size="small"
                  helperText="e.g., 0 = last reminder at cutoff"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Frequency (minutes)"
                  type="text"
                  value={form.frequencyMinutes}
                  onChange={e => handleNumberChange('frequencyMinutes', e.target.value)}
                  fullWidth
                  size="small"
                  helperText="e.g., 15 = every 15 minutes"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Notification Content */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight={600} color="text.primary" mb={2}>
              Notification Content
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Notification Title"
                  type="text"
                  value={form.title}
                  onChange={e => handleChange('title', e.target.value)}
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notification Body"
                  type="text"
                  value={form.body}
                  onChange={e => handleChange('body', e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Save Button */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                px: 4,
                py: 1.5,
              }}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
            {savedAt && (
              <Typography variant="body2" color="text.secondary">
                Saved at {savedAt.toLocaleTimeString()}
              </Typography>
            )}
          </Stack>
        </form>
      </Paper>

      {/* Preview Times */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h3" color="text.primary" mb={2}>
          Preview Reminder Times (Today)
        </Typography>
        {previewTimes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No valid times based on current settings.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {previewTimes.map((t, idx) => (
              <Card
                key={idx}
                variant="outlined"
                sx={{
                  minWidth: 140,
                  borderColor: 'primary.light',
                  backgroundColor: 'primary.light',
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" fontWeight={500} color="primary.dark">
                    {t.toLocaleTimeString()}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CutoffNotificationsSettings;